import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const ToastContext = createContext(null);

const TOAST_TYPE_META = {
  success: { title: 'Listo', icon: '✓' },
  error: { title: 'Error', icon: '⚠' },
  info: { title: 'Información', icon: 'ℹ' }
};

function buildToast(message, options = {}) {
  const type = options.type || 'error';
  const meta = TOAST_TYPE_META[type] || TOAST_TYPE_META.error;
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    title: options.title || meta.title,
    icon: options.icon || meta.icon,
    message,
    duration: Number.isFinite(options.duration) ? options.duration : undefined
  };
}

export function ToastProvider({ children, defaultDuration = 5000 }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const removeToast = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const pushToast = useCallback((message, options = {}) => {
    if (!message) return null;
    const toast = buildToast(message, options);
    const duration = Number.isFinite(toast.duration) ? toast.duration : defaultDuration;
    setToasts((current) => [...current, { ...toast, duration }]);
    const timer = setTimeout(() => removeToast(toast.id), duration);
    timersRef.current.set(toast.id, timer);
    return toast.id;
  }, [defaultDuration, removeToast]);

  const showError = useCallback((message, options = {}) => pushToast(message, { ...options, type: 'error' }), [pushToast]);
  const showSuccess = useCallback((message, options = {}) => pushToast(message, { ...options, type: 'success' }), [pushToast]);
  const showInfo = useCallback((message, options = {}) => pushToast(message, { ...options, type: 'info' }), [pushToast]);

  const value = useMemo(() => ({ pushToast, removeToast, showError, showSuccess, showInfo, defaultDuration }), [defaultDuration, pushToast, removeToast, showError, showSuccess, showInfo]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-region" aria-live="polite" aria-atomic="true">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast-card ${toast.type}`}>
            <div className="toast-icon" aria-hidden="true">{toast.icon}</div>
            <div className="toast-card-content">
              <strong className="toast-title">{toast.title}</strong>
              <p>{toast.message}</p>
            </div>
            <button type="button" className="toast-close-button" onClick={() => removeToast(toast.id)} aria-label="Cerrar notificación">×</button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast debe usarse dentro de ToastProvider');
  }
  return context;
}
